'use client';

import * as React from 'react';
import type { ReactElement, ReactNode } from 'react';
import { cx } from '../utils';
import { Eyebrow } from './Eyebrow';
import { useStableId } from '../hooks/useStableId';
import { IS_PROD, devWarn } from '../internal/devWarn';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';
import type { Density } from '../density';
import { FormDensityContext, FormDensityExplicitContext } from '../density';

export interface FormFieldProps extends StyledProps, WithChildren, UnsafeStyleProps {
  label?: ReactNode;
  span?: number;
  /* Explicit opt-out for rare callers whose child already labels
     itself (e.g. a nested <label>). Default is to auto-associate.

     NOTE ON CHILD SHAPE: the auto-association wires aria-labelledby
     onto the FIRST element child via React.cloneElement. Wrapping
     the form control in a layout div (e.g. <div><Input /></div>)
     puts the label on the wrapper, not the control — pass
     labelledBy={false} in that case and wire aria-labelledby
     yourself on the real control. */
  labelledBy?: false;
  /* Per-component density (ADR-008). When set, wraps the field
     output in a FormDensityContext.Provider so nested Field
     components inherit the density automatically. */
  density?: Density;
}

function isElement(child: ReactNode): child is ReactElement {
  return React.isValidElement(child);
}
/* Fragments are valid React elements but cloneElement's extra-props
   argument is a no-op on them — the props don't forward to the
   fragment's children. Detecting + warning is the only honest
   failure mode; we can't transparently walk into fragments. */
function isFragment(el: ReactElement): boolean {
  return el.type === React.Fragment;
}

export function FormField({
  label,
  children,
  span = 1,
  className,
  unsafe_style,
  labelledBy,
  density,
}: FormFieldProps) {
  const labelId = useStableId('jkit-form-field-label');
  const array = React.Children.toArray(children);
  const firstIdx = array.findIndex(isElement);
  const firstElement = firstIdx >= 0 ? (array[firstIdx] as ReactElement) : null;
  const shouldWire = label != null && labelledBy !== false && firstElement !== null;

  /* Warn at most once per mount when the first element child is a
     Fragment — cloneElement's props forward is a silent no-op
     there. firstElement is a fresh ReactElement on every parent
     render, so depending on it directly would re-fire the effect
     (and the warn) on every render of any parent that owns the
     FormField. The ref dedupes per mount. */
  const warnedRef = React.useRef(false);
  React.useEffect(() => {
    if (IS_PROD || warnedRef.current) return;
    if (!shouldWire || !firstElement) return;
    if (isFragment(firstElement)) {
      warnedRef.current = true;
      devWarn(
        '[JKit.FormField] the first child is a React.Fragment; the ' +
          'auto-associated aria-labelledby cannot forward through a ' +
          'fragment. Either make the form control a direct child, or ' +
          'pass labelledBy={false} and wire aria-labelledby yourself.',
      );
    }
  });

  const wiredChildren = shouldWire
    ? array.map((child, i) => {
        if (i !== firstIdx || !isElement(child)) return child;
        if (isFragment(child)) return child;
        const existingLabelledBy = (child.props as { 'aria-labelledby'?: string })[
          'aria-labelledby'
        ];
        const merged = existingLabelledBy ? `${existingLabelledBy} ${labelId}` : labelId;
        return React.cloneElement(child, { 'aria-labelledby': merged });
      })
    : array;

  const content = (
    <div
      className={cx('j-form-field', className)}
      style={{ gridColumn: `span ${span}`, ...unsafe_style }}
    >
      {label && (
        <Eyebrow>
          <span id={labelId}>{label}</span>
        </Eyebrow>
      )}
      {wiredChildren}
    </div>
  );

  if (density) {
    /* Two contexts: FormDensityContext (public) holds the value,
       FormDensityExplicitContext (internal) flags that a Form set
       it. Field uses the explicit flag to distinguish "Form told me
       'default'" from "no Form context at all". Codex audit #108 R9
       requires this split so the public hook can keep returning
       Density (not Density | undefined). */
    return (
      <FormDensityContext.Provider value={density}>
        <FormDensityExplicitContext.Provider value={true}>
          {content}
        </FormDensityExplicitContext.Provider>
      </FormDensityContext.Provider>
    );
  }

  return content;
}
